import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Upload, LogOut, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, logout, isAuthed } from "@/lib/api";
import { WORK_CATEGORY_KEYS, ASSET_TYPES, KNOWLEDGE_TOPIC_KEYS, PRODUCT_DOC_CATEGORIES } from "@/data/content";

const SECTIONS = [
  { value: "knowledge", label: "Knowledge Hub" },
  { value: "experience", label: "My Work" },
  { value: "product", label: "Product Documentation" },
];

const KNOWLEDGE_TYPES = ["Article", "PDF", "Presentation", "Template", "Playbook", "Case Study", "Research Note"];
const WORK_CATEGORIES = WORK_CATEGORY_KEYS;
const KNOWLEDGE_TOPICS = KNOWLEDGE_TOPIC_KEYS;
const EXPERIENCE_TYPES = ASSET_TYPES;
const PRODUCT_TYPES = ["Document", "PDF", "Presentation", "Diagram", "Note", "Spec"];

export default function Admin() {
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", section: "knowledge", category: KNOWLEDGE_TOPICS[0],
    content_type: "Article", tags: "", external_url: "", article_body: "", mode: "upload",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthed()) { nav("/login"); return; }
    api.get("/auth/me").catch(() => { logout(); nav("/login"); });
    load();
  }, []); // eslint-disable-line

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const catsFor = (s) => (s === "experience" ? WORK_CATEGORIES : s === "product" ? PRODUCT_DOC_CATEGORIES : KNOWLEDGE_TOPICS);
  const typesFor = (s) => (s === "experience" ? EXPERIENCE_TYPES : s === "product" ? PRODUCT_TYPES : KNOWLEDGE_TYPES);
  const categories = catsFor(form.section);
  const types = typesFor(form.section);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.mode === "upload") {
        if (!file) { toast.error("Please choose a file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", form.category);
        fd.append("section", form.section);
        fd.append("content_type", form.content_type);
        fd.append("tags", form.tags);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/documents", {
          title: form.title, description: form.description, category: form.category,
          section: form.section, content_type: form.content_type,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          external_url: form.external_url || null,
          article_body: form.article_body || null,
        });
      }
      toast.success("Content published");
      setForm((f) => ({ ...f, title: "", description: "", tags: "", external_url: "", article_body: "" }));
      setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const doLogout = () => { logout(); nav("/"); };

  const input = "w-full border border-line px-4 py-2.5 bg-paper outline-none focus:border-ink transition-colors text-sm";
  const lbl = "block font-mono-tech text-[10px] uppercase tracking-wider text-stone mb-1.5";

  return (
    <main className="pt-28 pb-24 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-auroraDeep mb-2">Content Studio</p>
            <h1 className="font-display font-bold text-4xl md:text-5xl">Manage content</h1>
          </div>
          <button onClick={doLogout} className="inline-flex items-center gap-2 border border-ink px-5 py-2.5 font-mono-tech text-xs uppercase tracking-wider hover:bg-ink hover:text-paper transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Form */}
          <form onSubmit={submit} className="border border-ink p-8 h-fit" data-testid="admin-form">
            <div className="flex gap-2 mb-6">
              {["upload", "link"].map((m) => (
                <button type="button" key={m} onClick={() => set("mode", m)} className={`flex-1 py-2 font-mono-tech text-[11px] uppercase tracking-wider border transition-colors ${form.mode === m ? "bg-ink text-paper border-ink" : "border-line text-stone"}`} data-testid={`mode-${m}`}>
                  {m === "upload" ? "Upload file" : "Link / Article"}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={lbl}>Section</label>
                <select value={form.section} onChange={(e) => { const s = e.target.value; setForm((f) => ({ ...f, section: s, category: catsFor(s)[0], content_type: typesFor(s)[0] })); }} className={input} data-testid="form-section">
                  {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Category</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={input} data-testid="form-category">
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className={lbl}>Title</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} required className={input} data-testid="form-title" />
            </div>

            <div className="mb-4">
              <label className={lbl}>Description</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={input} data-testid="form-description" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={lbl}>Type</label>
                <select value={form.content_type} onChange={(e) => set("content_type", e.target.value)} className={input} data-testid="form-type">
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Tags (comma separated)</label>
                <input value={form.tags} onChange={(e) => set("tags", e.target.value)} className={input} data-testid="form-tags" />
              </div>
            </div>

            {form.mode === "upload" ? (
              <div className="mb-6">
                <label className={lbl}>File (PDF, PPTX, image)</label>
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className={input} data-testid="form-file" />
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className={lbl}>External URL (optional)</label>
                  <input value={form.external_url} onChange={(e) => set("external_url", e.target.value)} placeholder="https://..." className={input} data-testid="form-url" />
                </div>
                <div className="mb-6">
                  <label className={lbl}>Article body (optional)</label>
                  <textarea value={form.article_body} onChange={(e) => set("article_body", e.target.value)} rows={4} className={input} data-testid="form-article" />
                </div>
              </>
            )}

            <button type="submit" disabled={busy} className="w-full bg-ink text-paper py-3.5 font-mono-tech text-xs uppercase tracking-[0.15em] inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="form-submit">
              {form.mode === "upload" ? <Upload size={14} /> : <Plus size={14} />} {busy ? "Publishing..." : "Publish content"}
            </button>
          </form>

          {/* List */}
          <div>
            <p className="font-mono-tech text-[11px] uppercase tracking-wider text-stone mb-4">Published — {docs.length}</p>
            <div className="space-y-2">
              {docs.length === 0 && <p className="text-stone font-light">No content yet.</p>}
              {docs.map((d) => (
                <div key={d.id} className="border border-line p-4 flex items-start justify-between gap-4" data-testid={`admin-doc-${d.id}`}>
                  <div className="min-w-0">
                    <p className="font-display font-medium text-lg truncate">{d.title}</p>
                    <p className="font-mono-tech text-[10px] uppercase tracking-wider text-stone mt-1">
                      {d.section} &middot; {d.category} &middot; {d.content_type}
                    </p>
                    {(d.file_path || d.external_url) && (
                      <a href={d.external_url || fileUrl(d.id)} target="_blank" rel="noreferrer" className="text-auroraDeep text-xs font-mono-tech mt-1 inline-block">open →</a>
                    )}
                  </div>
                  <button onClick={() => del(d.id)} className="text-stone hover:text-auroraDeep transition-colors shrink-0" data-testid={`delete-${d.id}`} aria-label="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
